import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { ChannelDetailsPanel } from "@/components/settings/ChannelDetailsPanel";
import { ChannelConnectDialog } from "@/components/contacts/ChannelConnectDialog";
import type { AssistantIdentity } from "@/types/contacts";

/**
 * `ChannelSettingsDialog` — the editor behind the business-card IM pencils
 * (redesign §3.5, operator 2026-07-27: the pencils must OPEN the thing that
 * edits that channel, including a gated disconnect).
 *
 * Nothing here is new machinery — it REHOSTS the shipped `ChannelDetailsPanel`
 * (identity, reveal-secret, send-test, rotate, and DISCONNECT behind the
 * typed-phrase `ConfirmDestructiveDialog` — D-9's "EU ENTENDO" gate),
 * decoupled from the settings shell via `useOpenclawSettingsOptional`. Rotate
 * hands over to the shipped connect dialog (upsert semantics).
 *
 * E-mail does NOT route here: its editor is the §3.9 `EmailSetupDialog`
 * (address + From-name + EP2 pairing; no disconnect — the mailbox is
 * provisioned, not connected, so offering one would be a lie).
 */
export function ChannelSettingsDialog({
  channel,
  assistant,
  onClose,
  onChanged,
}: {
  channel: "telegram" | "whatsapp";
  /** Kept for parity with the hero's dialog family (unused today — the
   *  details panel reads its own state). */
  assistant: AssistantIdentity | null;
  onClose: () => void;
  /** Re-read the card's live state after a disconnect. */
  onChanged?: () => void;
}) {
  const { t } = useTranslation();
  const [rotating, setRotating] = useState(false);
  void assistant;

  // Esc closes THIS dialog only. Capture-phase + stopImmediatePropagation so
  // FocusPaneHost's window Esc→closePane never also fires. While the
  // typed-phrase disconnect gate is stacked on top (it has NO Esc handling of
  // its own), Esc is swallowed rather than closing the host under it — the
  // gate's explicit Cancelar is the way out of a destructive confirm.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      if (document.querySelector('[data-testid="confirm-destructive-dialog"]')) return;
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // The rotate path hands over to the shipped connect dialog entirely.
  if (rotating) {
    return (
      <ChannelConnectDialog
        channel={channel}
        // ⚖ DECLARE THE INTENT — without this the plumbing is inert and the
        // dialog falls back to "connect", which is what produced the dead end:
        // a healthy channel is `complete`, so "Trocar token" opened a terminal
        // "Tudo pronto ✓" panel with no credential door and the lawyer had to
        // break his own channel to rotate. This branch IS the rotate path
        // (`rotating` is set by `ChannelDetailsPanel`'s rotate control), so the
        // intent is known here and nowhere below.
        intent="retrade"
        onClose={() => {
          setRotating(false);
          onChanged?.();
        }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid={`channel-settings-dialog-${channel}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("agent.hero.card.settingsTitle", { channel: t(`agent.sections.${channel}`) })}
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl dark:bg-coffee-900"
      >
        <div className="mb-3 flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="font-serif-display text-lg text-coffee-900 dark:text-cream-100">
              {t("agent.hero.card.settingsTitle", { channel: t(`agent.sections.${channel}`) })}
            </h2>
          </div>
          <button
            type="button"
            data-testid="channel-settings-dialog-close"
            onClick={onClose}
            aria-label={t("common.close")}
            title={t("common.close")}
            className="shrink-0 rounded-lg p-1.5 text-coffee-500 transition-colors hover:bg-cream-100 hover:text-coffee-800 dark:text-cream-500 dark:hover:bg-coffee-800 dark:hover:text-cream-100"
          >
            <X size={16} />
          </button>
        </div>

        <ChannelDetailsPanel
          id={channel}
          onRotate={() => setRotating(true)}
          onChanged={() => {
            onChanged?.();
            onClose();
          }}
        />
      </div>
    </div>
  );
}
